import dayjs from "dayjs";
import { OrderStatus } from "@/types/order/order.common";
import { zOrderDetailsDtoSchema, zOrderStatusHistoryDtoSchema, zChargingSessionDtoSchema, } from "@/types/orderDetails/orderDetails.dto";
/**
 * Mapper for OrderDetails: DTO → Domain.
 * Follows Order.mapper.ts conventions:
 * - Zod .parse() inside try/catch
 * - Robust status fallback (default to NEW on invalid/missing status)
 * - dayjs for timestamps (stored as strings in domain for serialisation safety)
 */
export const OrderDetailsMapper = {
    toDomain(raw) {
        try {
            const rawObj = raw;
            // Robust status validation (mirrors Order.mapper.ts pattern)
            const isValidStatus = Object.values(OrderStatus).includes(rawObj["status"]);
            if (!isValidStatus) {
                console.warn("OrderDetailsMapper: Invalid/Missing status:", rawObj["status"], "ID:", rawObj["id"], "- Defaulting to NEW");
                rawObj["status"] = OrderStatus.NEW;
            }
            const dto = zOrderDetailsDtoSchema.parse(rawObj);
            // Technician snapshot
            const technician = dto.technician
                ? {
                    id: dto.technician.id ?? null,
                    firstName: dto.technician.firstName ?? null,
                    lastName: dto.technician.lastName ?? null,
                    imgURL: dto.technician.imgURL ?? null,
                    rating: dto.technician.rating ?? null,
                    countReviews: dto.technician.count_reviews ?? null,
                }
                : null;
            // Vehicle snapshot
            const vehicle = dto.vehicle
                ? {
                    id: dto.vehicle.id ?? null,
                    name: dto.vehicle.name ?? null,
                    brand: dto.vehicle.brand ?? null,
                    model: dto.vehicle.model ?? null,
                    year: dto.vehicle.year ?? null,
                    stateNumber: dto.vehicle.stateNumber ?? null,
                }
                : null;
            // Review — field is `text`, NOT `comment`
            const review = dto.review
                ? {
                    id: dto.review.id ?? null,
                    rating: dto.review.rating ?? null,
                    text: dto.review.text ?? null,
                }
                : null;
            // Route points
            const routePoints = (dto.route_points ?? []).map((rp) => ({
                id: rp.id ?? null,
                addressShort: rp.addressShort ?? null,
                addressLocality: rp.addressLocality ?? null,
                address: rp.address ?? null,
                orderIndex: rp.orderIndex ?? null,
                latitude: rp.latitude ?? null,
                longitude: rp.longitude ?? null,
                plannedArrivalTime: rp.plannedArrivalTime ?? null,
                expectedArrivalTime: rp.expectedArrivalTime ?? null,
                actualArrivalTime: rp.actualArrivalTime ?? null,
                actualDepartureTime: rp.actualDepartureTime ?? null,
                updatedAt: rp.updatedAt ?? null,
            }));
            // Use aggregated/deduped photo arrays (photosBeforeCharging etc.) — ignore legacy raw arrays
            const photosBefore = dto.photosBeforeCharging ?? [];
            const photosIn = dto.photosInCharging ?? [];
            const photosAfter = dto.photosAfterCharging ?? [];
            return {
                id: dto.id,
                status: dto.status,
                orderNumber: dto.orderNumber ?? null,
                idClient: dto.idClient ?? null,
                idConversation: dto.idConversation ?? null,
                addressId: dto.addressId ?? null,
                addressShort: dto.addressShort ?? null,
                addressLocality: dto.addressLocality ?? null,
                address: dto.address ?? null,
                latitude: dto.latitude ?? null,
                longitude: dto.longitude ?? null,
                scheduledDate: dto.scheduledDate
                    ? dayjs(dto.scheduledDate).toISOString()
                    : null,
                scheduledDateEnd: dto.scheduledDateEnd
                    ? dayjs(dto.scheduledDateEnd).toISOString()
                    : null,
                arrivedAt: dto.arrived_at ? dayjs(dto.arrived_at).toISOString() : null,
                chargeValue: dto.chargeValue ?? null,
                startCharge: dto.startCharge ?? null,
                planTotalKwt: dto.planTotalKwt ?? null,
                planCost: dto.planCost ?? null,
                tariffPerKwt: dto.tariffPerKwt ?? null,
                cardId: dto.cardId ?? null,
                cardLast4: dto.cardLast4 ?? null,
                isPaid: dto.isPaid ?? null,
                // FR-007: плательщик и снимок тарифа (контракт v2.0 §8)
                payerType: dto.payerType ?? null,
                corpCompanyId: dto.corpCompanyId ?? null,
                corpCompanyName: dto.corpCompanyName ?? null,
                corpLinkId: dto.corpLinkId ?? null,
                pricePerKwhSnapshot: dto.pricePerKwhSnapshot ?? null,
                serviceFeeSnapshot: dto.serviceFeeSnapshot ?? null,
                isCorporate: dto.payerType === "COMPANY",
                technician,
                vehicle,
                review,
                liveCharging: {
                    currentCharge: dto.currentCharge ?? null,
                    currentTotalKwt: dto.currentTotalKwt ?? null,
                    currentCost: dto.currentCost ?? null,
                    currentLatitude: dto.currentLatitude ?? null,
                    currentLongitude: dto.currentLongitude ?? null,
                    chargingSeconds: dto.chargingSeconds ?? null,
                },
                completion: {
                    actualTotalKwt: dto.actualTotalKwt ?? null,
                    actualCost: dto.actualCost ?? null,
                    actualCharge: dto.actualCharge ?? null,
                    chargedAt: dto.charged_at ? dayjs(dto.charged_at).toISOString() : null,
                },
                cancellation: {
                    reasonCancellation: dto.reason_cancellation ?? null,
                    cancelledAt: dto.cancelled_at
                        ? dayjs(dto.cancelled_at).toISOString()
                        : null,
                },
                routePoints,
                photos: {
                    before: photosBefore.filter((p) => typeof p === "string"),
                    in: photosIn.filter((p) => typeof p === "string"),
                    after: photosAfter.filter((p) => typeof p === "string"),
                },
            };
        }
        catch (e) {
            console.error("OrderDetailsMapper.toDomain validation error", e);
            throw e;
        }
    },
    statusHistoryToDomain(raw) {
        try {
            const dto = zOrderStatusHistoryDtoSchema.parse(raw);
            return {
                id: dto.id,
                idOrder: dto.idOrder ?? null,
                idTechnician: dto.idTechnician ?? null,
                previousStatus: dto.previousStatus ?? null,
                newStatus: dto.newStatus ?? null,
                reason: dto.reason ?? null,
                latitude: dto.latitude ?? null,
                longitude: dto.longitude ?? null,
                createdAt: dto.createdAt ?? null,
                updatedAt: dto.updatedAt ?? null,
            };
        }
        catch (e) {
            console.error("OrderDetailsMapper.statusHistoryToDomain validation error", e);
            throw e;
        }
    },
    chargingSessionToDomain(raw) {
        try {
            const dto = zChargingSessionDtoSchema.parse(raw);
            return {
                sessionId: dto.session_id,
                transactionId: dto.transaction_id ?? null,
                connectorId: dto.connector_id ?? null,
                routePointId: dto.route_point_id ?? null,
                startTime: dto.start_time ?? null,
                endTime: dto.end_time ?? null,
                startMeterValue: dto.start_meter_value ?? null,
                endMeterValue: dto.end_meter_value ?? null,
                startSoc: dto.start_soc ?? null,
                endSoc: dto.end_soc ?? null,
                maxPower: dto.max_power ?? null,
                avgPower: dto.avg_power ?? null,
                status: dto.status ?? null,
                stopReason: dto.stop_reason ?? null,
                errorCode: dto.error_code ?? null,
                meterValuesCount: dto.meter_values_count ?? null,
                chargingStationUuid: dto.charging_station_uuid ?? null,
                energyDelivered: dto.energy_delivered ?? null,
                createdAt: dto.created_at ?? null,
                updatedAt: dto.updated_at ?? null,
            };
        }
        catch (e) {
            console.error("OrderDetailsMapper.chargingSessionToDomain validation error", e);
            throw e;
        }
    },
};
